import {
    IVirtualKeyboardDetector,
    VirtualKeyboardData,
    VKEYBOARD_ANIMATION_DURATION,
} from '@/utils/virtual_keyboard_detector';
import { IJumpToBottom } from './jump_to_bottom';
import { HEADER_ID, SEARCH_INPUT_ID } from './config';
import { getFocusedElement } from '@/utils/general';
import { disableBodyScroll, enableBodyScroll } from '@/utils/body_scroll_lock';

export interface IContentWrapper {
    setJumpToBottom(jumpToBottom: IJumpToBottom): void;
    open(): void;
    close(): void;
    setElements(wrapperElement: HTMLElement, searchResults: HTMLElement): void;
    openIfFocused(): void;
}

export class ContentWrapper implements IContentWrapper {
    wrapperElement: HTMLElement;
    jumpToBottom: IJumpToBottom;
    isOpen = false;
    isMobile: boolean;
    height: string;
    searchResults: HTMLElement;
    virtualKeyboardDetector: IVirtualKeyboardDetector;
    virtualKeyboardListenerId: string;

    constructor(isMobile: boolean, virtualKeyboardDetector: IVirtualKeyboardDetector) {
        this.isMobile = isMobile;
        this.virtualKeyboardDetector = virtualKeyboardDetector;
    }

    createVirtualKeyboardListener() {
        this.virtualKeyboardListenerId = this.virtualKeyboardDetector.createListener(
            this.onVirtualKeyboardChange.bind(this),
            this.isMobile
        );
    }

    removeVirtualKeyboardListener() {
        this.virtualKeyboardDetector.removeListener(this.virtualKeyboardListenerId);
    }

    onVirtualKeyboardChange(event: VirtualKeyboardData) {
        const height = event.visibleHeight - this.getHeaderHeight();
        this.setElementHeight(height);

        if (this.jumpToBottom) {
            // We need to set the visibility of the jumpToBottom button after the animation is done
            setTimeout(() => {
                this.jumpToBottom.setVisibility();
            }, VKEYBOARD_ANIMATION_DURATION);
        }
    }

    handleScroll() {
        this.jumpToBottom.setVisibility();
    }

    open() {
        if (this.isOpen) {
            return;
        }

        this.isOpen = true;
        this.jumpToBottom.setVisibility();

        if (this.isMobile) {
            this.createVirtualKeyboardListener();
            setTimeout(() => {
                disableBodyScroll(this.searchResults);
            }, VKEYBOARD_ANIMATION_DURATION);
        }
    }

    close() {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;

        if (this.isMobile) {
            this.removeVirtualKeyboardListener();
            enableBodyScroll(this.searchResults);
        }
    }

    setJumpToBottom(jumpToBottom: IJumpToBottom) {
        this.jumpToBottom = jumpToBottom;
    }

    getHeaderHeight() {
        const header = document.querySelector(`#${HEADER_ID}`) as HTMLElement;
        return header ? header.clientHeight : 0;
    }

    // The search input can possibly be focused before Alpine loads.
    // We open the dropdown if the search input is focused when the component is initialized.
    openIfFocused() {
        const focusedElement = getFocusedElement();

        if (focusedElement && focusedElement.id === SEARCH_INPUT_ID) {
            this.open();
        }
    }

    setElements(wrapperElement: HTMLElement, searchResults: HTMLElement) {
        this.wrapperElement = wrapperElement;
        this.searchResults = searchResults;
    }

    setElementHeight(height: number) {
        if (!this.wrapperElement) {
            return;
        }

        this.height = `${height}px`;
    }
}
